/**
 * Pre-launch signup modal: restores the result after the customer form's POST/redirect.
 *
 * `{% form 'customer' %}` navigates, so the outcome only exists on the page we land back on.
 * Liquid cannot tell our form's post apart from the footer email-signup's — `form.id` is
 * always blank on a customer form — so both render a result. A sessionStorage flag set at
 * submit time decides which one was ours.
 *
 * The dialog opens from three places: a `coming-soon-cta` button, any purchase control
 * (intercepted below), and any link to `#notify-me` — for nav items with no product behind
 * them. Links are inert unless coming-soon mode is on.
 */

const DIALOG_ID = 'coming-soon-dialog';
const SUBMIT_FLAG = 'cbz-coming-soon-submitted';
const SUBSCRIBED_FLAG = 'cbz-coming-soon-subscribed';
const FRAGMENT = '#ComingSoonSignup';

// Opens the dialog from an ordinary link, for nav items that have no product to point at.
// Deliberately NOT `FRAGMENT`: that one marks the form's own POST/redirect, so reusing it
// would re-open the dialog on top of the confirmation the redirect just rendered.
const LINK_FRAGMENT = '#notify-me';

// A submit that detours via /challenge never comes back with `customer_posted`, so the flag
// would otherwise sit there and fire on some unrelated later page.
const FLAG_TTL = 30_000;

/** Marks the next page load as ours, so we claim the result instead of the footer signup. */
function trackSubmit() {
  const form = document.querySelector('.coming-soon-dialog__form');
  if (!form) return;

  form.addEventListener('submit', () => {
    try {
      sessionStorage.setItem(SUBMIT_FLAG, String(Date.now()));
    } catch {
      // Private mode or blocked storage — the URL fragment is the fallback signal.
    }
  });
}

/** True when the page we landed on is the redirect from our own submit. */
function isOurPost() {
  let flagged = false;

  try {
    const stamp = Number(sessionStorage.getItem(SUBMIT_FLAG));
    if (stamp) {
      sessionStorage.removeItem(SUBMIT_FLAG);
      flagged = Date.now() - stamp < FLAG_TTL;
    }
  } catch {
    // Ignore — fall through to the fragment check.
  }

  if (!new URLSearchParams(window.location.search).has('customer_posted')) return false;

  return flagged || window.location.hash === FRAGMENT;
}

/** Collapses the form down to the confirmation. Safe to call on any page. */
function markSubscribed() {
  const form = /** @type {HTMLElement | null} */ (document.querySelector('.coming-soon-dialog__form'));
  if (!form) return;

  form.dataset.state = 'success';

  const message = /** @type {HTMLElement | null} */ (
    document.querySelector('[data-coming-soon-message="success"]')
  );
  if (message) message.hidden = false;
}

/** True once this browser has completed a signup. */
function isSubscribed() {
  try {
    return localStorage.getItem(SUBSCRIBED_FLAG) === '1';
  } catch {
    return false;
  }
}

async function openDialog() {
  await customElements.whenDefined('dialog-component');

  const dialog = /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (
    document.getElementById(DIALOG_ID)
  );
  if (!dialog) return;

  customElements.upgrade(dialog);
  dialog.showDialog?.();
}

async function restoreResult() {
  const result = /** @type {HTMLElement | null} */ (document.querySelector('[data-coming-soon-result]'));
  if (!result) return;

  // An email that is already subscribed comes back as neither success nor error.
  const state = result.dataset.comingSoonResult?.trim() === 'error' ? 'error' : 'success';
  const message = /** @type {HTMLElement | null} */ (
    document.querySelector(`[data-coming-soon-message="${state}"]`)
  );

  if (message) message.hidden = false;

  // Nothing left to fill in once they are on the list. Remembered so the form stays collapsed
  // on later page loads, not just the one we land on after the redirect.
  if (state === 'success') {
    markSubscribed();
    try {
      localStorage.setItem(SUBSCRIBED_FLAG, '1');
    } catch {
      // Blocked storage — the collapsed state just won't survive navigation.
    }
  }

  // The footer email-signup renders its own result for our post. Remove the duplicate.
  for (const stray of document.querySelectorAll('.email-signup__message')) {
    stray.remove();
  }

  await openDialog();

  // showDialog() calls showModal() inside a rAF, which moves focus — announce after it.
  requestAnimationFrame(() => message?.focus());

  window.history.replaceState(null, '', window.location.pathname);
}

// Every control that would otherwise write to the cart. Capture-phase interception keeps the
// markup (and therefore the styling) exactly as the theme renders it.
const PURCHASE_CONTROLS = [
  'add-to-cart-component button',
  '.quick-add__button',
  'button[name="add"]',
  'button[name="checkout"]',
  '.shopify-payment-button__button',
].join(',');

function interceptPurchaseClicks() {
  window.addEventListener(
    'click',
    (event) => {
      const target = /** @type {Element | null} */ (event.target);
      if (!target?.closest) return;
      if (!target.closest(PURCHASE_CONTROLS)) return;
      if (target.closest('.coming-soon-dialog')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      openDialog();
    },
    { capture: true }
  );
}

/** Same page, so the browser would only move the scroll position — we can open instead. */
function isSameDocument(/** @type {HTMLAnchorElement} */ anchor) {
  return anchor.pathname === window.location.pathname && anchor.search === window.location.search;
}

/**
 * Opens the dialog from `#notify-me` links. Bubble phase and anchor-only, so it cannot
 * collide with the capture-phase purchase interception above (those targets are buttons).
 * A link pointing at another page is left alone — `openFromHash` picks it up on arrival.
 */
function interceptSignupLinks() {
  document.addEventListener('click', (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const anchor = /** @type {HTMLAnchorElement | null} */ (target?.closest?.('a[href]') ?? null);
    if (!anchor || anchor.hash !== LINK_FRAGMENT) return;
    if (!isSameDocument(anchor)) return;

    // Coming-soon mode off means no dialog in the DOM; let the link behave normally.
    if (!document.getElementById(DIALOG_ID)) return;

    event.preventDefault();
    openDialog();
  });
}

/** Handles a `#notify-me` link that navigated here from another page. */
function openFromHash() {
  if (window.location.hash !== LINK_FRAGMENT) return;
  if (!document.getElementById(DIALOG_ID)) return;

  openDialog();
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

function init() {
  trackSubmit();

  if (isOurPost()) {
    restoreResult();
    return;
  }

  if (isSubscribed()) {
    markSubscribed();
  }

  openFromHash();
}

// Registered immediately: window capture listeners fire in registration order, so this must
// beat any other script that claims the same clicks.
interceptPurchaseClicks();
interceptSignupLinks();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
